/** Shapes the equipment screens read. Mirrors `server/src/routes/equipment.ts`. */

export interface EquipmentIdentity {
  id: string
  assetTag: string
  name: string
  /** What a proposal prints for a quote line naming this piece (0126); null is `name`. */
  customerName: string | null
  status: 'available' | 'in_shop' | 'out_of_service' | 'retired'
  statusReason: string | null
  equipmentTypeId: string | null
  typeName: string | null
  /* The type's mark, so the record header draws the tile the list draws. */
  typeIcon: string | null
  typeColour: string | null
  family: string | null
  meterKind: 'none' | 'miles' | 'hours'
  orgUnitId: string
  orgUnitName: string | null
  year: number | null
  make: string | null
  model: string | null
  vin: string | null
  plate: string | null
  plateExpiresOn: string | null
  gvwrLb: number | null
  curbWeightLb: number | null
  decaledOn: string | null
  purchasedOn: string | null
  purchaseCost: string
  salvageValue: string
  taxMethod: string
  bookMethod: string
  bookLifeMonths: number
  internalHourlyRate: string | null
  billableRate: string | null
  billableUnit: string
  /** The kind's row on the catalog's Equipment tab (0129), which is what a quote charges. */
  kindId?: string | null
  kindName?: string | null
  kindPrice?: string | null
  kindUnit?: string | null
  serviceTemplateId: string | null
  serviceTemplateName: string | null
  homeYardId: string | null
  notes: string
  version: number
}

export interface ServiceLine {
  lineId: string
  name: string
  basis: 'miles' | 'hours' | 'months'
  interval: number
  progress: number
  remaining: number
  due: boolean
  overdue: boolean
  dueOn: string | null
}

export interface WorkOrder {
  id: string
  reference: string
  title: string
  detail: string
  status: string
  origin: 'manual' | 'quick_check' | 'service_due'
  scheduledFor: string | null
  takesOutOfService: boolean
  laborHours: string
  partsCost: string
  openedAt: string
  closedAt: string | null
  assignedToName: string | null
}

export interface QuickCheckSummary {
  id: string
  submittedAt: string
  meterReading: string | null
  fuelLevel: string | null
  byName: string | null
  issues: number
}

export interface EquipmentDetail {
  equipment: EquipmentIdentity
  mechanic: { userId: string; name: string; orgUnitName: string } | null
  scope: { orgUnitId: string; name: string; kind: string }[]
  pairedWith: { id: string; name: string; assetTag: string; curbWeightLb: number | null }[]
  livesWith: { id: string; name: string } | null
  service: ServiceLine[]
  health: { score: number; reasons: string[] }
  workOrders: WorkOrder[]
  quickChecks: QuickCheckSummary[]
  meterReadings: { reading: string; kind: string; recordedAt: string; source: string }[]
  depreciation: Record<string, { taken: string; bookValue: string | null }>
  usage: { hours: string; visits: number; cost: string }
}

// --- morning assignment -------------------------------------------------------

export type NeedStatus = 'auto' | 'assigned' | 'choose' | 'conflict'

export interface AssignmentCandidate {
  equipmentId: string
  name: string
  assetTag: string
  freeFrom: string | null
  freeUntil: string | null
  orgUnitName: string | null
  unavailable: string | null
  /** Set when this crew already has it booked today, so the row can undo it. */
  assignmentId: string | null
  assignmentVisitId: string | null
}

export interface EquipmentNeed {
  key: string
  source: 'crew' | 'service' | 'paired'
  label: string
  detail: string
  visitId: string | null
  equipmentId: string | null
  qty: number
  status: NeedStatus
  chosen: string[]
  candidates: AssignmentCandidate[]
  rental: { id: string; name: string; price: string; unit: string } | null
}

export interface CrewAssignment {
  crewId: string
  crewName: string
  visitCount: number
  visits: { id: string; jobTitle: string; propertyName: string }[]
  needs: EquipmentNeed[]
  status: 'ready' | 'choose' | 'conflict'
}

export interface PoolRow {
  equipmentId: string
  name: string
  status: string
  bookings: {
    visitId: string | null
    crewName: string | null
    from: string
    to: string
    reason: string
  }[]
}

export interface MorningAssignment {
  date: string
  crews: CrewAssignment[]
  summary: { needs: number; auto: number; choose: number; conflicts: number }
  pool: PoolRow[]
}

/** What the status badge says, and how it reads. */
/**
 * The words, and nothing else (`marks-and-icons.md` §5).
 *
 * It carried a `tone` as well, which is what let two screens draw the same
 * status in two different colours — a tone is three buckets and the equipment
 * palette has five pairs, so the mapping lost information on the way through.
 * The colour comes from `--status-equipment-<status>-*` now, which is where
 * every other group's colour has always come from.
 */
export const EQUIPMENT_STATUS: Record<string, { label: string }> = {
  available: { label: 'Available' },
  in_shop: { label: 'In shop' },
  out_of_service: { label: 'Out of service' },
  retired: { label: 'Retired' },
}

/** The check form the crew fills in. */
export interface QuickCheckForm {
  equipment: {
    id: string
    name: string
    assetTag: string
    meterKind: 'none' | 'miles' | 'hours'
    lastReading: string | null
    lastReadAt: string | null
  }
  items: { id: string; label: string; hint: string }[]
  pairedWith: { id: string; name: string }[]
  mechanic: { userId: string; name: string; orgUnitName: string } | null
}
